// src/services/simpleErrorTracking.js
// Lightweight error tracking without external dependencies
import { performanceAnalytics } from './performanceAnalytics';
import { supabase } from '../config';

class SimpleErrorTracking {
  constructor() {
    this.isInitialized = false;
    this.config = {
      environment: process.env.NODE_ENV,
      release: process.env.REACT_APP_VERSION || '1.0.0',
      enabled: true // Always enabled for basic tracking
    };
    this.errors = [];
    this.maxErrors = 100; // Keep only last 100 errors
  }

  // Initialize error tracking
  async initialize() {
    if (this.isInitialized) return;

    try {
      // Set up global error handlers
      this.setupGlobalErrorHandlers();
      this.isInitialized = true;
      console.log('✅ Simple error tracking initialized');
    } catch (error) {
      console.error('Failed to initialize error tracking:', error);
    }
  }

  // Set up global error handlers
  setupGlobalErrorHandlers() {
    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.captureError(new Error(event.reason), {
        type: 'unhandled_promise_rejection',
        reason: event.reason
      });
    });

    // Global JavaScript errors
    window.addEventListener('error', (event) => {
      this.captureError(event.error, {
        type: 'global_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });
  }

  // Capture error
  captureError(error, context = {}) {
    const errorData = {
      id: Date.now() + Math.random(),
      message: error.message || 'Unknown error',
      stack: error.stack,
      context,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      performance: performanceAnalytics.getPerformanceSummary()
    };

    // Store error locally
    this.errors.push(errorData);
    
    // Keep only recent errors
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(-this.maxErrors);
    }

    // Log to console with details
    console.group('🚨 Error Captured');
    console.error('Message:', errorData.message);
    console.error('Context:', errorData.context);
    console.error('Stack:', errorData.stack);
    console.error('URL:', errorData.url);
    console.error('Performance:', errorData.performance);
    console.groupEnd();

    // Log to database (audit_log table)
    this.logToDatabase(errorData);

    // In production, you could send to your own API endpoint
    if (process.env.NODE_ENV === 'production') {
      this.sendToOwnAPI(errorData);
    }
  }

  // Log error to database (audit_log table)
  async logToDatabase(errorData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('audit_log')
        .insert({
          table_name: 'error_log',
          record_id: errorData.id?.toString() || 'unknown',
          operation: 'INSERT',
          new_values: {
            message: errorData.message,
            stack: errorData.stack,
            context: errorData.context,
            url: errorData.url,
            user_agent: errorData.userAgent,
            performance: errorData.performance
          },
          user_id: user?.id || null,
          timestamp: new Date().toISOString(),
          user_agent: errorData.userAgent
        });

      if (error) {
        console.warn('Failed to log error to database:', error);
      }
    } catch (err) {
      console.warn('Error logging to database:', err);
    }
  }

  // Send to your own API endpoint (implement as needed)
  async sendToOwnAPI(errorData) {
    try {
      // Example: Send to your own error logging endpoint
      // await fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorData)
      // });
      console.log('Error would be sent to API:', errorData.id);
    } catch (error) {
      console.error('Failed to send error to API:', error);
    }
  }

  // Capture message
  captureMessage(message, level = 'info', context = {}) {
    const messageData = {
      id: Date.now() + Math.random(),
      message,
      level,
      context,
      timestamp: new Date().toISOString(),
      url: window.location.href
    };

    console.log(`📝 Message captured (${level}):`, messageData);
  }

  // Set user context
  setUser(user) {
    const userData = {
      id: user.id,
      email: user.email,
      username: (user.user_metadata?.first_name && user.user_metadata?.last_name)
        ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
        : user.user_metadata?.first_name || user.user_metadata?.last_name || user.email?.split('@')[0]
    };

    console.log('👤 User context set:', userData);
    
    // Store user context for error reports
    this.userContext = userData;
  }

  // Set custom tags
  setTags(tags) {
    console.log('🏷️ Tags set:', tags);
    this.tags = { ...this.tags, ...tags };
  }

  // Track performance issues
  trackPerformanceIssue(componentName, metrics) {
    const issue = {
      type: 'performance_issue',
      component: componentName,
      metrics,
      timestamp: new Date().toISOString()
    };

    this.captureMessage(`Performance issue in ${componentName}`, 'warning', issue);
  }

  // Track API errors
  trackApiError(endpoint, method, error, response) {
    const apiError = {
      type: 'api_error',
      endpoint,
      method,
      error: error.message,
      status: response?.status,
      timestamp: new Date().toISOString()
    };

    this.captureError(error, apiError);
  }

  // Get error summary
  getErrorSummary() {
    const summary = {
      totalErrors: this.errors.length,
      recentErrors: this.errors.slice(-10),
      errorTypes: {},
      userContext: this.userContext,
      tags: this.tags
    };

    // Count error types
    this.errors.forEach(error => {
      const type = error.context.type || 'unknown';
      summary.errorTypes[type] = (summary.errorTypes[type] || 0) + 1;
    });

    return summary;
  }

  // Export error data
  exportErrorData() {
    const summary = this.getErrorSummary();
    
    if (process.env.NODE_ENV === 'development') {
      console.group('📊 Error Tracking Summary');
      console.log('Total Errors:', summary.totalErrors);
      console.table(summary.errorTypes);
      console.log('Recent Errors:', summary.recentErrors);
      console.groupEnd();
    }
    
    return summary;
  }

  // Clear old errors
  clearOldErrors(maxAge = 24 * 60 * 60 * 1000) { // 24 hours
    const cutoff = Date.now() - maxAge;
    this.errors = this.errors.filter(error => 
      new Date(error.timestamp).getTime() > cutoff
    );
  }

  // Get tracking status
  getStatus() {
    return {
      initialized: this.isInitialized,
      errorCount: this.errors.length,
      userContext: !!this.userContext,
      config: {
        environment: this.config.environment,
        release: this.config.release,
        enabled: this.config.enabled
      }
    };
  }
}

// Singleton instance
export const simpleErrorTracking = new SimpleErrorTracking();

// Auto-initialize
simpleErrorTracking.initialize();

// Auto-clear old errors every hour
setInterval(() => {
  simpleErrorTracking.clearOldErrors();
}, 60 * 60 * 1000);

export default simpleErrorTracking;
